import { Injectable, OnDestroy } from '@angular/core';
import {
    DateRange,
    DeepPartial,
    DisplayNotice,
    FlyFreelyError,
    FlyFreelyLoggingService,
    GqlSortField,
    NoticeCriteria,
    NoticeDto,
    NoticeImportance,
    NoticesService,
    patchableState,
    PersonsOrganisationDto,
    setState,
    StateUpdateCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { ColumnSortPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import {
    CurrentOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { BehaviorSubject, combineLatest, ReplaySubject, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import Audience = NoticeDto.Audience;

export const formatLotamPriority = {
    [NoticeImportance.LOW]: 'Low',
    [NoticeImportance.MEDIUM]: 'Medium',
    [NoticeImportance.HIGH]: 'High'
};

export const formatLotamAudience = {
    [Audience.GENERAL]: 'General',
    [Audience.FLIGHT_OPERATIONS]: 'Flight Operations'
};

export const formatOperatingRequirement: Record<string, string> = {
    NONE: 'None',
    ACKNOWLEDGE: 'Acknowledge',
    BLOCK: 'Block',
    AUTOMATIC: 'Automatic'
};

interface SearchParameters {
    criteria: NoticeCriteria;
    page: number;
    limit: number;
    sorting?: ColumnSortPreferences;
}

@Injectable()
export class LotamDataService implements OnDestroy {
    currentOrganisation?: PersonsOrganisationDto;

    private lotamSource = new ReplaySubject<DisplayNotice[]>(1);
    private workingSource = new BehaviorSubject<boolean>(false);
    private totalItemsSource = new ReplaySubject<number>(1);
    // This is currently displayed page, rather than the last requested page
    private currentPageSource = new BehaviorSubject<number>(0);

    private searchSubject = new Subject<
        StateUpdateCommand<DeepPartial<SearchParameters>>
    >();

    search$ = patchableState(this.searchSubject, {
        limit: 10,
        page: 0,
        criteria: {}
    });

    working$ = this.workingSource.asObservable();
    lotamSource$ = this.lotamSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();
    searchCriteria$ = this.search$.pipe(
        map(s => s.criteria),
        filter(c => c?.organisationId != null)
    );

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private workspaceStateService: WorkspaceStateService,
        private logging: FlyFreelyLoggingService,
        private noticesService: NoticesService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        combineLatest([
            this.workspaceStateService.currentLoadedOrganisation$,
            this.search$.pipe(
                filter(
                    (c): c is SearchParameters =>
                        c.criteria?.organisationId != null &&
                        c.limit != null &&
                        c.page != null
                )
            )
        ]).subscribe(([organisation, search]) => {
            this.currentOrganisation = (
                organisation as CurrentOrganisation
            ).organisation;
            if (this.currentOrganisation != null) {
                this.updateLotamsData(
                    search.criteria,
                    search.page,
                    search.limit,
                    search.sorting
                );
            } else {
                this.lotamSource.next([]);
            }
        });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.workingSource.complete();
        this.searchSubject.complete();
    }

    findLotams({
        page,
        limit,
        organisationId,
        sorting,
        date,
        subject,
        description,
        importance,
        acknowledged,
        text,
        noticeTypeId
    }: {
        page: number;
        limit: number;
        organisationId: number;
        sorting?: ColumnSortPreferences;
        date?: DateRange;
        subject?: string;
        description?: string;
        importance?: NoticeImportance;
        acknowledged?: boolean;
        text?: string;
        noticeTypeId?: number;
    }) {
        this.searchSubject.next(
            setState({
                criteria: {
                    organisationId,
                    date,
                    subject,
                    description,
                    importance,
                    acknowledged,
                    text,
                    noticeTypeId
                },
                page,
                limit,
                sorting
            })
        );
    }

    private updateLotamsData(
        criteria: {
            organisationId: number;
            time?: DateRange;
            subject?: string;
            description?: string;
            importance?: NoticeImportance;
            acknowledged?: boolean;
            text?: string;
            noticeTypeId?: number;
        },
        page: number,
        limit: number,
        sorting?: ColumnSortPreferences
    ) {
        const doneWorking = this.workTracker.createTracker();
        const sortFields = this.buildSortFields(sorting);
        this.noticesService
            .findNotices(
                {
                    ...criteria,
                    audience: 'FLIGHT_OPERATIONS'
                },
                {
                    page: page,
                    pageSize: limit,
                    sortFields
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: notices => {
                    this.totalItemsSource.next(notices.count);
                    this.currentPageSource.next(page);
                    this.lotamSource.next(notices.results);
                    doneWorking();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading LOTAMs: ${error.message}`
                    );
                    this.lotamSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                    doneWorking();
                }
            });
    }

    private static readonly sortFieldMap: Record<string, string> = {
        'noticeType.name': 'noticeType'
    };

    private buildSortFields(sorting?: ColumnSortPreferences): GqlSortField[] {
        if (sorting?.column == null) {
            return [];
        }
        return [
            {
                field:
                    LotamDataService.sortFieldMap[sorting.column] ??
                    sorting.column,
                order: sorting.ascending ? 'ASC' : 'DESC'
            }
        ];
    }
}
